'use client';

import { memo, type FC } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { ChevronRight, Clock } from 'lucide-react';
import { m, useReducedMotion } from 'motion/react';

import { URLBuilder } from '@/utils/URLBuilder';

import { ProposalBadgeList, type TFaIconBadgeBadge } from '../proposal-badge-list/proposal-basge-list';
import { Heading } from '../ui/heading/heading';
import { formatDate } from '@/helpers/formatDate';
import type { TApiImage } from '@/shared/apiComponentTypes';
import { getImageSource } from '@/shared/image';
import { proposalCardHover, proposalImageHover } from '@/utils/animation/variants';
import { CardAccentLine } from '../cards/shared/card-accent-line';
import { Button } from '../ui/button/button';

type TBlogPostCard = {
  image: TApiImage['src'];
  slug: string;
  title: string;
  publishedAt: string;
  tags?: TFaIconBadgeBadge[];
  labels: {
    dateAddedLabel: string;
  };
  readingTime?: number;
};

export const BlogPostCard: FC<TBlogPostCard> = memo(function BlogPostCard({
  title,
  image,
  slug,
  publishedAt,
  labels: { dateAddedLabel },
  tags = [],
  readingTime,
}) {
  const formattedDate = formatDate(publishedAt);
  const coverImage = getImageSource(image, ['small', 'thumbnail']);
  const prefersReducedMotion = useReducedMotion();

  if (!coverImage) return null;

  const motionProps = prefersReducedMotion
    ? {}
    : {
        initial: 'rest',
        whileHover: 'hover',
        animate: 'rest',
      };

  const firstTag = tags[0];

  return (
    <Link href={URLBuilder.paths.getBlogPost(slug)} className="group block h-full">
      <m.article
        variants={prefersReducedMotion ? undefined : proposalCardHover}
        {...motionProps}
        className="relative bg-backgroundLight border border-neutral-800 rounded-xl overflow-hidden flex flex-col lg:flex-row h-full lg:h-80 transition-colors hover:border-action/30"
      >
        {/* Image section with diagonal clip - matching the auction lot card */}
        <div className="relative lg:basis-1/2 shrink-0 h-56 lg:h-full overflow-hidden">
          {/* Diagonal clip mask for automotive feel */}
          <div className="absolute inset-0 lg:[clip-path:polygon(0_0,100%_0,85%_100%,0_100%)]">
            <m.div variants={prefersReducedMotion ? undefined : proposalImageHover} className="h-full w-full">
              <Image
                src={coverImage.url}
                width={coverImage.width}
                height={coverImage.height}
                alt={coverImage.alt || title.concat(' - Zdjecie glowne')}
                sizes="(max-width: 1024px) 100vw, 50vw"
                className="h-full w-full object-cover"
              />
            </m.div>
          </div>

          {/* Automotive gradient overlay */}
          <div className="absolute inset-0 bg-automotive-gradient opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

          {/* Category pill in top-left */}
          {firstTag && (
            <div className="absolute top-4 left-4 z-10">
              {/* Same pill as the auction card's source label, so the three card
                  families share one badge language; the gold dot keeps the
                  category accent the underline used to carry. */}
              <span className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-background/80 px-3 py-1.5 text-xs font-oswald uppercase tracking-wider text-neutral-200 backdrop-blur-md shadow-lg shadow-black/30">
                <span aria-hidden className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-action" />
                {firstTag.label}
              </span>
            </div>
          )}

          {/* Reading time indicator in bottom-left */}
          {readingTime && (
            <div className="absolute bottom-4 left-4 z-10">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-background/80 px-3 py-1.5 text-xs font-oswald uppercase tracking-wider text-neutral-200 backdrop-blur-md shadow-lg shadow-black/30">
                <Clock size={14} className="text-action" aria-hidden />
                {readingTime} min
              </span>
            </div>
          )}
        </div>

        {/* Content section */}
        <div className="relative flex flex-grow flex-col p-5 lg:block lg:basis-1/2 lg:-ml-[7.5%] lg:pt-0 lg:pb-24 lg:pl-4 lg:pr-5">
          {/* Zero-content float whose `shape-outside` mirrors the diagonal cut of
              the photo, so every line box (title, badges, price) starts where the
              photo's edge is at that height and the text hugs the slant. Only
              inline-level content flows around a shape, hence the badge list is
              inline-blocks here and the bottom bar is pinned out of flow. */}
          <div
            aria-hidden
            className="hidden lg:block float-left h-[calc(100%+6rem)] w-[13%] [shape-outside:polygon(0_0,100%_0,0_100%)] [shape-margin:0.75rem]"
          />

          <Heading
            variant="h2"
            className="text-xl md:text-2xl lg:text-3xl transition-colors group-hover:text-action max-lg:line-clamp-2 mb-3 lg:pt-5"
          >
            {title}
          </Heading>

          {/* Tags (excluding first since it's shown as category) */}
          {tags.length > 1 && (
            <div className="mb-4 lg:mb-2 lg:[&>ul]:block lg:[&>ul>li]:mb-2 lg:[&>ul>li]:mr-2 lg:[&>ul>li]:inline-block lg:[&>ul>li]:align-top">
              <ProposalBadgeList badges={tags.slice(1, 3)} variant="sm" />
            </div>
          )}

          {/* Date at bottom */}
          <div className="mt-auto pt-4 border-t border-neutral-800/50 flex items-center justify-between lg:absolute lg:bottom-5 lg:left-9 lg:right-5 lg:mt-0">
            <div>
              <p className="font-roboto text-xs text-neutral-500 uppercase tracking-wider">
                {dateAddedLabel}
              </p>
              <p className="font-oswald text-sm text-neutral-300">{formattedDate}</p>
            </div>

            {/* Arrow indicator: the Button's face on a span (a button inside a
                link is not allowed), lit by the CARD's hover — fill, sheen and
                all — so the whole card reads as one control. */}
            <Button
              as="span"
              iconOnly
              variant="secondary"
              startIcon={<ChevronRight size={18} aria-hidden />}
              className="h-10 shrink-0 px-4 group-hover:border-action group-hover:bg-action group-hover:text-background group-hover:before:opacity-100 motion-safe:group-hover:before:translate-x-[380%]"
            />
          </div>
        </div>

        {/* Golden accent line on left */}
        <CardAccentLine position="left" />
      </m.article>
    </Link>
  );
});
